import React, { useCallback } from 'react';
import { CodeNameModel } from 'components/common/types';
import CollapsibleList from 'lib/CollapsibleList';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import SelectOption from 'lib/SelectOption';
import { TruncatedElementWithTooltip } from 'components/common/TruncatedElementWithTooltip/TruncatedElementWithTooltip';
import { shouldDisplayCategoryClearAll } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';
import { LocationOptionsListProps } from './LocationOptionsList.types';
import useIncrementalRender from './useIncrementalRender';

type LocationItemProps = {
  subOption: CodeNameModel;
  category: string;
  hideExclude?: boolean;
  isSelected: boolean;
  include: boolean | undefined;
  onOptionIncludeExcludeChange: (option: CodeNameModel) => void;
};

const LocationItem = React.memo<LocationItemProps>(
  ({ subOption, category, hideExclude, isSelected, include, onOptionIncludeExcludeChange }) => {
    const handleChange = useCallback(
      (value: CodeNameModel) =>
        onOptionIncludeExcludeChange({
          ...value,
          category,
          ...(subOption.mediaOwner ? { mediaOwner: subOption.mediaOwner } : {}),
        }),
      [onOptionIncludeExcludeChange, category, subOption.mediaOwner],
    );

    return (
      <SelectOption
        value={{ code: subOption.code, name: subOption.name, include }}
        isSelected={isSelected}
        isDisabled={false}
        isMultiple
        hasCheckbox={false}
        hideExclude={hideExclude}
        isIncludeExcludeAlwaysVisible
        onIncludeExcludeChange={handleChange}
        classNames="!m-0"
      >
        <TruncatedElementWithTooltip tooltip={subOption.nameWithMediaOwner ?? subOption.name}>
          {(ref) => (
            <p ref={ref} className="max-w-[250px] truncate">
              {subOption.nameWithMediaOwner ?? subOption.name}
            </p>
          )}
        </TruncatedElementWithTooltip>
      </SelectOption>
    );
  },
);

const getItemKey = (subOption: CodeNameModel): string =>
  subOption.mediaOwner ? `${subOption.code}-${subOption.mediaOwner}` : subOption.code;

const CategoryItems: React.FC<{
  items: CodeNameModel[];
  category: string;
  hideExclude?: boolean;
  isOptionSelected: (code: string, category: string, mediaOwner?: string) => boolean;
  isOptionIncluded: (code: string, category: string, mediaOwner?: string) => boolean;
  onOptionIncludeExcludeChange: (option: CodeNameModel) => void;
}> = ({ items, category, hideExclude, isOptionSelected, isOptionIncluded, onOptionIncludeExcludeChange }) => {
  const { visibleCount, loadMoreTriggerRef, hasMore } = useIncrementalRender(items.length);

  return (
    <>
      {items.slice(0, visibleCount).map((subOption) => {
        const isSelected = isOptionSelected(subOption.code, category, subOption.mediaOwner);

        return (
          <LocationItem
            key={getItemKey(subOption)}
            subOption={subOption}
            category={category}
            hideExclude={hideExclude}
            isSelected={isSelected}
            include={isSelected ? isOptionIncluded(subOption.code, category, subOption.mediaOwner) : undefined}
            onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
          />
        );
      })}
      {hasMore && <div ref={loadMoreTriggerRef} />}
    </>
  );
};

const LocationOptionsList: React.FC<LocationOptionsListProps> = ({
  filteredOptions,
  selectedOptions,
  openCategories,
  sortDirection,
  includedOptions,
  excludedOptions,
  hideExclude,
  onSort,
  onToggleCategory,
  setSelectedOptions,
  getNumberOfFilteredOptions,
  isOptionSelected,
  isOptionIncluded,
  onOptionIncludeExcludeChange,
  onCategoryIncludeAll,
  onCategoryExcludeAll,
  getCategoryIncludeState,
}) => {
  return (
    <>
      {Object.keys(filteredOptions).map((category) => {
        const items = filteredOptions[category];

        return (
          <CollapsibleList
            label={category}
            isSortVisible
            onSort={onSort}
            isClearAllVisible={shouldDisplayCategoryClearAll(selectedOptions, category)}
            onClearAll={() => setSelectedOptions((prev) => prev.filter((loc) => loc.category !== category))}
            isOpen={openCategories.includes(category)}
            onToggle={() => onToggleCategory(category)}
            sortDir={sortDirection}
            key={`location-category-${category}`}
            groupIncludeState={getCategoryIncludeState(category)}
            onGroupInclude={() => onCategoryIncludeAll(category)}
            onGroupExclude={() => onCategoryExcludeAll(category)}
            hideGroupExclude={hideExclude}
            headerComponent={
              <CollapsibleListStatsHeader
                available={items.length}
                included={getNumberOfFilteredOptions(category, includedOptions)}
                excluded={getNumberOfFilteredOptions(category, excludedOptions)}
              />
            }
          >
            <CategoryItems
              items={items}
              category={category}
              hideExclude={hideExclude}
              isOptionSelected={isOptionSelected}
              isOptionIncluded={isOptionIncluded}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
            />
          </CollapsibleList>
        );
      })}
    </>
  );
};

export default LocationOptionsList;
